import React, { useEffect, useState } from 'react';
import { Trash2, Mail, Phone, Building, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { fetchContactSubmissions, deleteSubmission } from '../utils/supabase';

// Hash function for password verification
function generateSyncHash(str) {
  let hash = 0;
  if (!str || str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return hash;
}

const ADMIN_PASSWORD_HASH = 1569205073; // Hash of "Rohith@123456"

const ClientDetails = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(true);
  const [isHashGeneratorMode, setIsHashGeneratorMode] = useState(false);

  useEffect(() => {
    // Check for hash generation request
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("generate_hash")) {
      const testPassword = urlParams.get("generate_hash");
      const calculatedHash = generateSyncHash(testPassword);
      setIsHashGeneratorMode(true);
      setShowPasswordModal(false);
      setLoading(false);
      alert(`[HASH GENERATOR]\nPassword: ${testPassword}\nHash: ${calculatedHash}`);
      console.log(`Password: "${testPassword}" => Hash: ${calculatedHash}`);
      return;
    }
    
    // Check for bypass parameter in URL
    if (urlParams.get("pass")) {
      const enteredHash = generateSyncHash(urlParams.get("pass"));
      console.log(`Entered password hash: ${enteredHash}, Expected: ${ADMIN_PASSWORD_HASH}`);
      if (enteredHash === ADMIN_PASSWORD_HASH) {
        console.warn(`%c [AUTHENTICATED] `, 'color: #0f0; font-weight: bold;');
        setIsAuthenticated(true);
        setShowPasswordModal(false);
        loadSubmissions();
        return;
      }
    }
    
    // If not authenticated via URL, show password modal
    setLoading(false);
  }, []);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    const enteredHash = generateSyncHash(passwordInput);
    
    if (enteredHash === ADMIN_PASSWORD_HASH) {
      setIsAuthenticated(true);
      setShowPasswordModal(false);
      setPasswordInput('');
      loadSubmissions();
      toast.success('Access Granted');
    } else {
      toast.error('Invalid Password');
      setPasswordInput('');
    }
  };

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const data = await fetchContactSubmissions();
      setSubmissions(data);
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      setDeleting(prev => ({ ...prev, [id]: true }));
      await deleteSubmission(id);
      setSubmissions(prev => prev.filter(s => s.id !== id));
      toast.success('Submission deleted');
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hash Generator Mode */}
      {isHashGeneratorMode && (
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md border-2 border-green-500">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-green-600 mb-4">✓ Hash Generated</h2>
              <p className="text-slate-600">Check the alert and console for the hash value.</p>
              <Button 
                onClick={() => window.location.href = '/client-details'} 
                className="mt-4"
              >
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Password Protection Modal */}
      {showPasswordModal && !isHashGeneratorMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md border-2 border-slate-900">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Admin Access</h2>
              <p className="text-slate-600 mb-6">This is a restricted admin page. Please enter the password to continue.</p>
              
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter admin password"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800">
                  Verify Password
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content - Only show if authenticated */}
      {isAuthenticated && (
        <>
          {/* Header */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h1 className="text-4xl font-bold mb-2">Client Submissions</h1>
              <p className="text-slate-300">Manage all contact form submissions</p>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600 text-lg">Loading submissions...</p>
          </div>
        ) : submissions.length === 0 ? (
          <Card className="border-none shadow-lg">
            <CardContent className="p-12 text-center">
              <p className="text-slate-600 text-lg mb-4">No submissions yet</p>
              <p className="text-slate-500">Contact form submissions will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-slate-700 font-semibold">Total Submissions: {submissions.length}</p>
            </div>

            {submissions.map((submission) => (
              <Card key={submission.id} className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    {/* Organization */}
                    <div className="flex items-start space-x-3">
                      <Building className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-600 font-semibold">Organization</p>
                        <p className="text-slate-900 font-medium">{submission.org_name}</p>
                      </div>
                    </div>

                    {/* Name */}
                    <div className="flex items-start space-x-3">
                      <div className="h-5 w-5 bg-slate-300 rounded mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-600 font-semibold">Contact Name</p>
                        <p className="text-slate-900 font-medium">{submission.name}</p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start space-x-3">
                      <Mail className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-600 font-semibold">Email</p>
                        <a href={`mailto:${submission.email}`} className="text-blue-600 hover:text-blue-700 font-medium">
                          {submission.email}
                        </a>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-start space-x-3">
                      <Phone className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-600 font-semibold">Phone</p>
                        <a href={`tel:${submission.phone}`} className="text-blue-600 hover:text-blue-700 font-medium">
                          {submission.phone}
                        </a>
                      </div>
                    </div>

                    {/* Services */}
                    <div className="flex items-start space-x-3">
                      <div className="h-5 w-5 bg-slate-300 rounded mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-600 font-semibold">Services Interested</p>
                        <p className="text-slate-900 font-medium capitalize">{submission.services}</p>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-start space-x-3">
                      <Calendar className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-600 font-semibold">Submitted</p>
                        <p className="text-slate-900 font-medium">{formatDate(submission.submitted_at)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  {submission.message && (
                    <div className="mb-4 pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-600 font-semibold mb-2">Message</p>
                      <p className="text-slate-700 bg-slate-50 p-3 rounded">{submission.message}</p>
                    </div>
                  )}

                  {/* Delete Button */}
                  <div className="flex justify-end pt-4 border-t border-slate-200">
                    <Button
                      onClick={() => handleDelete(submission.id)}
                      disabled={deleting[submission.id]}
                      variant="destructive"
                      className="flex items-center space-x-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>{deleting[submission.id] ? 'Deleting...' : 'Delete'}</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </div>
        </>
      )}
    </div>
  );
};

export default ClientDetails;
